/**
 * AssetProfileCopilot — V22.9.6 side panel for the Asset Profile Gate.
 *
 * Two cards:
 *   1. "Suggested RTO/RPO" — pulls from /api/ai-copilot/asset/suggest-rto
 *      (deterministic, free, refreshes on every step / field change)
 *   2. "Criticality Reasoning" — pulls from /api/ai-copilot/asset/explain-criticality
 *      (LLM, only fired when isFinalStep && form has minimum required fields)
 *
 * Hybrid budget per the V22.9.6 design: zero LLM cost during data entry, one LLM
 * call per profile when the user reaches the review step.
 */
import { useEffect, useState, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { Skeleton } from "./ui/skeleton";
import { Sparkles, Gauge, Brain, AlertCircle } from "lucide-react";

export default function AssetProfileCopilot({ api, form, blueprint, isFinalStep }) {
  const [rto, setRto] = useState(null);
  const [reasoning, setReasoning] = useState(null);
  const [loadingRto, setLoadingRto] = useState(false);
  const [loadingReasoning, setLoadingReasoning] = useState(false);

  // Suggest-RTO — fires on every relevant field change (deterministic, free)
  const fetchRto = useCallback(async () => {
    if (!blueprint?.asset_type) return;
    setLoadingRto(true);
    try {
      const res = await api.post("/ai-copilot/asset/suggest-rto", {
        asset_type: blueprint.asset_type,
        asset_name: form.asset_name || null,
        business_criticality: form.business_criticality || "medium",
        tier: form.tier || "tier_3",
        it_function_scope: form.it_function_scope || null,
        hosting_type: form.hosting_type || null,
      });
      setRto(res.data || null);
    } catch (e) {
      setRto(null);
    } finally {
      setLoadingRto(false);
    }
  }, [api, blueprint, form.asset_type, form.business_criticality, form.tier, form.it_function_scope, form.hosting_type, form.asset_name]);

  useEffect(() => {
    const t = setTimeout(fetchRto, 350);
    return () => clearTimeout(t);
  }, [fetchRto]);

  // Explain-criticality — only on the final review step + only if minimum fields are present
  useEffect(() => {
    if (!isFinalStep || !form.asset_name) return;
    let cancelled = false;
    (async () => {
      setLoadingReasoning(true);
      try {
        const res = await api.post("/ai-copilot/asset/explain-criticality", {
          asset_name: form.asset_name,
          asset_type: blueprint?.asset_type || "other",
          regulatory_tags: form.compliance_tags || [],
          downstream_count: (form.downstream_asset_ids || []).length,
        });
        if (!cancelled) setReasoning(res.data || null);
      } catch (e) {
        if (!cancelled) setReasoning(null);
      } finally {
        if (!cancelled) setLoadingReasoning(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isFinalStep, api, blueprint, form.asset_name, form.compliance_tags, form.downstream_asset_ids]);

  return (
    <aside className="space-y-3" data-testid="asset-profile-copilot">
      <div className="flex items-center gap-1.5">
        <Badge variant="outline" className="text-violet-700 border-violet-200 bg-violet-50 text-[10px]">
          <Sparkles className="w-3 h-3 mr-1" /> Copilot
        </Badge>
        <span className="text-[10px] text-slate-400">v22.9.6 · hints update as you type</span>
      </div>

      {/* RTO/RPO suggestion card */}
      <Card className="border-l-4 border-l-violet-500" data-testid="copilot-rto-card">
        <CardHeader className="p-3 pb-1.5">
          <CardTitle className="text-xs flex items-center gap-1.5 text-slate-700">
            <Gauge className="w-3.5 h-3.5 text-violet-600" /> Suggested RTO / RPO
          </CardTitle>
        </CardHeader>
        <CardContent className="px-3 pb-3 pt-0">
          {loadingRto ? (
            <Skeleton className="h-12 w-full" />
          ) : rto ? (
            <div>
              <div className="flex gap-3 mb-1.5">
                <div>
                  <div className="text-[10px] text-slate-400 uppercase tracking-wide">RTO</div>
                  <div className="text-lg font-bold text-violet-700" data-testid="copilot-rto-value">{rto.rto_hours}h</div>
                </div>
                <div>
                  <div className="text-[10px] text-slate-400 uppercase tracking-wide">RPO</div>
                  <div className="text-lg font-bold text-violet-700" data-testid="copilot-rpo-value">{rto.rpo_hours}h</div>
                </div>
              </div>
              <p className="text-[11px] text-slate-600 leading-snug">{rto.reasoning}</p>
            </div>
          ) : (
            <p className="text-[11px] text-slate-400 italic">Fill in the criticality field to see a suggestion.</p>
          )}
        </CardContent>
      </Card>

      {/* Criticality reasoning — only on the final step */}
      {isFinalStep && (
        <Card className="border-l-4 border-l-sky-500" data-testid="copilot-reasoning-card">
          <CardHeader className="p-3 pb-1.5">
            <CardTitle className="text-xs flex items-center gap-1.5 text-slate-700">
              <Brain className="w-3.5 h-3.5 text-sky-600" /> Criticality Reasoning
            </CardTitle>
          </CardHeader>
          <CardContent className="px-3 pb-3 pt-0">
            {loadingReasoning ? (
              <Skeleton className="h-16 w-full" />
            ) : reasoning?.data ? (
              <div>
                <div className="flex items-center gap-1.5 mb-1.5">
                  <Badge variant="outline" className="text-[9px] uppercase tracking-wide bg-sky-50 border-sky-200 text-sky-700">Tier {reasoning.data.derived_tier} · {reasoning.data.tier_label}</Badge>
                  <Badge variant="outline" className="text-[9px] text-slate-500">{reasoning.source}</Badge>
                </div>
                <p className="text-[11px] text-slate-600 leading-snug" data-testid="copilot-reasoning-text">{reasoning.data.reasoning}</p>
                {reasoning.data.key_risks?.length > 0 && (
                  <div className="mt-2">
                    <div className="text-[10px] text-slate-400 uppercase tracking-wide mb-1">Key Risks</div>
                    <ul className="text-[11px] text-slate-600 list-disc pl-4 space-y-0.5">
                      {reasoning.data.key_risks.slice(0, 4).map((r, i) => <li key={i}>{r}</li>)}
                    </ul>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-start gap-1.5 text-[11px] text-slate-400 italic">
                <AlertCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                <span>Reasoning will appear once you've named the asset and selected criticality.</span>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <p className="text-[10px] text-slate-400 leading-snug px-1">
        Suggestions update as you type. Final review pulls a one-time LLM explanation. Every hint is audit-logged for ISO 22301 §7.5.
      </p>
    </aside>
  );
}
